import React from "react";
import { Box, InputAdornment, InputLabel, TextField } from "@mui/material";
import CostSlider from "./CostSlider";
import PercentageTuneSlider from "./PercentageTuneSlider";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { dt } from "../../../../shared/themes/redesignTokens";
import { getUiLocale } from "../../../../shared/utils/relativeTime";

interface PricingInput {
  label: string;
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  placeholder?: string;
  adornment?: string;
  type?: "currency" | "number";
  autoFocus?: boolean;
}

interface NoteField {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}

interface PricingLineItemProps {
  title: string;
  inputs: PricingInput[];
  computedTotal?: number;
  sliderValue: number;
  onSliderChange: (adjustment: number) => void;
  finalTotal: number;
  noteField?: NoteField;
  showSlider?: boolean;
  /** If true, use percentage-based slider (0-100%) instead of +/- EUR slider */
  usePercentageSlider?: boolean;
  /** Base value for percentage slider calculation */
  percentageBaseValue?: number;
  /** Unit label for percentage slider preview (e.g., "km", "hrs", "EUR") */
  percentageUnit?: string;
  /** Whether this field requires manual input (not auto-filled) */
  needsManualInput?: boolean;
  /** Additional content rendered below the title row */
  titleExtra?: React.ReactNode;
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat(getUiLocale(), {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const PricingLineItem = ({
  title,
  inputs,
  computedTotal,
  sliderValue,
  onSliderChange,
  finalTotal,
  noteField,
  showSlider = true,
  usePercentageSlider = false,
  percentageBaseValue,
  percentageUnit,
  needsManualInput,
  titleExtra,
}: PricingLineItemProps) => {
  const { translate } = useTranslation(["serviceOffers"]);

  // Check if any input field is empty (for warm background highlight)
  const hasEmptyFields = needsManualInput && inputs.some((i) => i.value === undefined || i.value === null);

  return (
    <Box
      sx={{
        background: dt.cardBg,
        border: `1px solid ${dt.cardBorder}`,
        borderRadius: dt.radiusSm,
        p: 2.5,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        // Manual input highlight: amber left border
        ...(needsManualInput
          ? {
              borderLeft: "3px solid #F59E0B",
            }
          : {}),
      }}
    >
      {/* Title row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "14px",
              fontWeight: 600,
              color: dt.textPrimary,
            }}
          >
            {title}
          </Box>
          {/* Manual pill badge */}
          {needsManualInput && (
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "10px",
                fontWeight: 600,
                color: "#92400E",
                backgroundColor: "#FEF3C7",
                borderRadius: "10px",
                px: 1,
                py: 0.25,
                lineHeight: 1.4,
                textTransform: "uppercase",
                letterSpacing: "0.03em",
              }}
            >
              {translate("travelInfo.manualInput")}
            </Box>
          )}
        </Box>
        <Box
          sx={{
            fontFamily: dt.fontMono,
            fontSize: "16px",
            fontWeight: 700,
            color: dt.blue,
          }}
        >
          {formatCurrency(finalTotal)}
        </Box>
      </Box>

      {/* Title extra content (e.g., change departure link, travel source info) */}
      {titleExtra}

      {/* Input fields */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        {inputs.map((input, idx) => {
          const isEmpty = input.value === undefined || input.value === null;
          return (
            <Box key={idx} sx={{ flex: "1 1 140px", minWidth: "120px" }}>
              <InputLabel
                sx={{
                  fontFamily: dt.font,
                  fontSize: "12px",
                  fontWeight: 500,
                  color: dt.textSecondary,
                  mb: 0.5,
                }}
              >
                {input.label}
              </InputLabel>
              <TextField
                size="small"
                type="number"
                placeholder={input.placeholder || "0"}
                autoFocus={input.autoFocus}
                value={input.value ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  input.onChange(v === "" ? undefined : Number(v));
                }}
                InputProps={{
                  startAdornment: input.type === "currency" ? (
                    <InputAdornment position="start">&euro;</InputAdornment>
                  ) : input.adornment ? (
                    <InputAdornment position="start">{input.adornment}</InputAdornment>
                  ) : undefined,
                }}
                sx={{
                  width: "100%",
                  "& .MuiOutlinedInput-root": {
                    fontFamily: dt.fontMono,
                    fontSize: "13px",
                    borderRadius: dt.radiusSm,
                    // Warm background for empty fields when manual input needed
                    ...(needsManualInput && isEmpty
                      ? {
                          backgroundColor: "#FFFBEB",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#FCD34D",
                          },
                        }
                      : {}),
                  },
                }}
              />
            </Box>
          );
        })}
      </Box>

      {/* Note field (optional) */}
      {noteField && (
        <TextField
          size="small"
          fullWidth
          placeholder={noteField.placeholder}
          value={noteField.value}
          onChange={(e) => noteField.onChange(e.target.value)}
          sx={{
            "& .MuiOutlinedInput-root": {
              fontFamily: dt.font,
              fontSize: "13px",
              borderRadius: dt.radiusSm,
            },
          }}
        />
      )}

      {/* Computed subtotal (if applicable) */}
      {computedTotal !== undefined && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            fontFamily: dt.fontMono,
            fontSize: "12px",
            color: dt.textSecondary,
          }}
        >
          Subtotal: {formatCurrency(computedTotal)}
        </Box>
      )}

      {/* Slider */}
      {showSlider !== false && usePercentageSlider && (
        <PercentageTuneSlider
          value={sliderValue}
          baseValue={percentageBaseValue ?? computedTotal ?? finalTotal}
          onChange={onSliderChange}
          unit={percentageUnit}
        />
      )}
      {showSlider !== false && !usePercentageSlider && (
        <CostSlider
          value={sliderValue}
          baseValue={computedTotal ?? finalTotal}
          onChange={onSliderChange}
          label={translate("percentageSlider.fineTuneEurLabel")}
        />
      )}
    </Box>
  );
};

export default PricingLineItem;
